import { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Input, InputNumber, Select, message, Statistic, Row, Col, Tabs, Progress, Alert, Tooltip, Popconfirm, Typography, Empty, Divider } from 'antd';
import { SafetyCertificateOutlined, ReloadOutlined, DeleteOutlined, ClockCircleOutlined, ThunderboltOutlined, UserOutlined, KeyOutlined, GlobalOutlined, ApiOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { adminAPI, internalGatewayAPI } from '../services/api';
import { t } from '../utils/i18n';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

const RateLimits = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [overview, setOverview] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [keyStats, setKeyStats] = useState([]);
  const [ipStats, setIpStats] = useState([]);
  const [modelStats, setModelStats] = useState([]);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [userLimitModalVisible, setUserLimitModalVisible] = useState(false);
  const [modelLimitModalVisible, setModelLimitModalVisible] = useState(false);
  const [editingModel, setEditingModel] = useState(null); // 正在编辑的模型名称（null 表示新增）
  const [modelLimitForm] = Form.useForm();
  const [form] = Form.useForm();
  const [userLimitForm] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, statsRes] = await Promise.all([
        internalGatewayAPI.get('/admin/ratelimit'),
        internalGatewayAPI.get('/admin/ratelimit/stats'),
      ]);

      if (configRes?.data) {
        setConfig(configRes.data);
      } else if (configRes && !configRes.data) {
        setConfig(configRes);
      }
      if (statsRes?.data) {
        setOverview(statsRes.data);
      } else if (statsRes && !statsRes.data) {
        setOverview(statsRes);
      }
      setTopUsers([]);
      setBlockedUsers([]);
      setUserStats([]);
      setKeyStats([]);
      setIpStats([]);
      setModelStats([]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      message.error('获取用量数据失败，请检查 internal-gateway 服务是否运行');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async (values) => {
    try {
      await internalGatewayAPI.put('/admin/ratelimit', values);
      message.success(t('success'));
      setConfigModalVisible(false);
      fetchData();
    } catch (error) {
      message.error(t('error') + ': ' + (error.message || 'Failed to update config'));
    }
  };

  const handleResetLimit = async (type, id, model = null) => {
    try {
      let url = `/admin/ratelimit/reset?type=${type}&id=${id}`;
      if (model) url += `&model=${model}`;
      await internalGatewayAPI.post(url);
      message.success(t('success'));
      fetchData();
    } catch (error) {
      message.error(t('error') + ': ' + (error.message || 'Failed to reset'));
    }
  };

  const handleApplyUserLimit = async (values) => {
    try {
      await internalGatewayAPI.post('/admin/usage/user-limit', values);
      message.success(t('success'));
      setUserLimitModalVisible(false);
      userLimitForm.resetFields();
    } catch (error) {
      message.error(t('error') + ': ' + (error.message || 'Failed to apply limit'));
    }
  };

  const handleAddModelLimit = async (values) => {
    try {
      // 构建 model_limit 对象
      const modelLimit = {};
      modelLimit[values.model] = {
        limit: values.limit,
        window: values.window,
      };
      await internalGatewayAPI.put('/admin/ratelimit', { model_limit: modelLimit });
      message.success(editingModel ? t('model_edited_success') : t('success'));
      setModelLimitModalVisible(false);
      setEditingModel(null);
      modelLimitForm.resetFields();
      fetchData();
    } catch (error) {
      message.error(t('error') + ': ' + (error.message || 'Failed to add model limit'));
    }
  };

  const handleEditModelLimit = (model, limit, window) => {
    setEditingModel(model);
    modelLimitForm.setFieldsValue({
      model: model,
      limit: limit,
      window: window,
    });
    setModelLimitModalVisible(true);
  };

  const handleOpenAddModelLimit = () => {
    setEditingModel(null);
    modelLimitForm.resetFields();
    setModelLimitModalVisible(true);
  };

  const handleRemoveModelLimit = async (model) => {
    try {
      // 将 limit 设为 0 表示移除该模型的限流配置
      const modelLimit = {};
      modelLimit[model] = {
        limit: 0,
        window: 0,
      };
      await internalGatewayAPI.put('/admin/ratelimit', { model_limit: modelLimit });
      message.success(t('success'));
      fetchData();
    } catch (error) {
      message.error(t('error') + ': ' + (error.message || 'Failed to remove model limit'));
    }
  };

  const userColumns = [
    {
      title: t('user_id'),
      dataIndex: 'user_id',
      key: 'user_id',
      width: 150,
      render: (text) => <Text style={{ fontFamily: 'monospace' }}>{text}</Text>,
    },
    {
      title: t('requests_in_window'),
      dataIndex: 'reqs_window',
      key: 'reqs_window',
      width: 100,
      render: (val) => <span style={{ color: val > 50 ? '#ff4d4f' : '#52c41a' }}>{val}</span>,
    },
    {
      title: t('limit'),
      dataIndex: 'limit',
      key: 'limit',
      width: 80,
    },
    {
      title: t('usage_rate'),
      dataIndex: 'usage_rate',
      key: 'usage_rate',
      width: 150,
      render: (val) => <Progress percent={Math.min(val, 100)} size="small" status={val > 80 ? 'exception' : 'active'} />,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => {
            userLimitForm.setFieldsValue({ user_id: record.user_id });
            setUserLimitModalVisible(true);
          }}>
            {t('edit')}
          </Button>
          <Popconfirm title={t('confirm_reset')} onConfirm={() => handleResetLimit('user', record.user_id)}>
            <Button size="small" danger icon={<ReloadOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const keyColumns = [
    {
      title: 'API Key',
      dataIndex: 'key',
      key: 'key',
      width: 200,
      render: (text) => <Text copyable={{ text: text }} style={{ fontFamily: 'monospace', fontSize: 11 }} ellipsis={{ showTitle: false }}>{text}</Text>,
    },
    {
      title: t('requests_in_window'),
      dataIndex: 'reqs_window',
      key: 'reqs_window',
      width: 100,
    },
    {
      title: t('limit'),
      dataIndex: 'limit',
      key: 'limit',
      width: 80,
      render: (val) => val || '-',
    },
    {
      title: t('usage_rate'),
      dataIndex: 'usage_rate',
      key: 'usage_rate',
      width: 150,
      render: (val) => <Progress percent={Math.min(val, 100)} size="small" />,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm title={t('confirm_reset')} onConfirm={() => handleResetLimit('key', record.key)}>
          <Button size="small" danger icon={<ReloadOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const ipColumns = [
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 150,
      render: (text) => <Text style={{ fontFamily: 'monospace' }}>{text}</Text>,
    },
    {
      title: t('requests_in_window'),
      dataIndex: 'reqs_window',
      key: 'reqs_window',
      width: 100,
    },
    {
      title: t('limit'),
      dataIndex: 'limit',
      key: 'limit',
      width: 80,
      render: (val) => val || '-',
    },
    {
      title: t('usage_rate'),
      dataIndex: 'usage_rate',
      key: 'usage_rate',
      width: 150,
      render: (val) => <Progress percent={Math.min(val, 100)} size="small" />,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm title={t('confirm_reset')} onConfirm={() => handleResetLimit('ip', record.ip)}>
          <Button size="small" danger icon={<ReloadOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const modelColumns = [
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 180,
      render: (text, record) => {
        // user_id 格式为 "user:126" 或 "ip:192.168.1.1"
        const idParts = record.user_id.split(':');
        const idType = idParts[0];
        const idValue = idParts.slice(1).join(':');
        return (
          <Space direction="vertical" size={0}>
            <Tag color="blue">{text}</Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {idType === 'user' ? '用户' : 'IP'}: {idValue}
            </Text>
          </Space>
        );
      },
    },
    {
      title: t('requests_in_window'),
      dataIndex: 'reqs_window',
      key: 'reqs_window',
      width: 100,
    },
    {
      title: t('limit'),
      dataIndex: 'limit',
      key: 'limit',
      width: 80,
    },
    {
      title: t('usage_rate'),
      dataIndex: 'usage_rate',
      key: 'usage_rate',
      width: 150,
      render: (val) => <Progress percent={Math.min(val, 100)} size="small" />,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm title={t('confirm_reset')} onConfirm={() => handleResetLimit('model', record.user_id, record.model)}>
          <Button size="small" danger icon={<ReloadOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  // 模型限流配置列表（从配置中获取已配置的模型限流）
  const modelLimitConfigs = config?.model_limit ? Object.entries(config.model_limit).map(([model, cfg]) => ({
    model,
    limit: cfg.limit,
    window: cfg.window,
  })) : [];

  const tabItems = [
    {
      key: 'users',
      label: <span><UserOutlined /> {t('user_list')}</span>,
      children: (
        <Table
          columns={userColumns}
          dataSource={userStats}
          rowKey="user_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      ),
    },
    {
      key: 'keys',
      label: <span><KeyOutlined /> API Keys</span>,
      children: (
        <Table
          columns={keyColumns}
          dataSource={keyStats}
          rowKey="key"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      ),
    },
    {
      key: 'ips',
      label: <span><GlobalOutlined /> IPs</span>,
      children: (
        <Table
          columns={ipColumns}
          dataSource={ipStats}
          rowKey="ip"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      ),
    },
    {
      key: 'models',
      label: <span><ApiOutlined /> {t('model_list')}</span>,
      children: (
        <>
          {/* 已配置的模型限流列表 */}
          {modelLimitConfigs.length > 0 ? (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={[8, 8]} align="middle">
                <Col>
                  <Text strong>{t('configured_model_limits')}: </Text>
                </Col>
                {modelLimitConfigs.map(({ model, limit, window }) => (
                  <Col key={model}>
                    <Space>
                      <Tag 
                        color="orange" 
                        closable 
                        onClose={() => handleRemoveModelLimit(model)}
                        style={{ fontSize: 12, padding: '4px 8px' }}
                      >
                        {model}: {limit}次/{window}s
                      </Tag>
                      <Tooltip title={t('edit')}>
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<EditOutlined />}
                          onClick={() => handleEditModelLimit(model, limit, window)}
                        />
                      </Tooltip>
                    </Space>
                  </Col>
                ))}
                <Col>
                  <Button 
                    type="dashed" 
                    size="small" 
                    icon={<PlusOutlined />}
                    onClick={handleOpenAddModelLimit}
                  >
                    {t('add')}
                  </Button>
                </Col>
              </Row>
            </Card>
          ) : (
            <Alert
              message={t('no_model_limits_configured')}
              description={<Button type="primary" icon={<PlusOutlined />} onClick={() => setModelLimitModalVisible(true)}>{t('add_model_limit')}</Button>}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            columns={modelColumns}
            dataSource={modelStats}
            rowKey={(record) => `${record.model}:${record.user_id}`}
            loading={loading}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: <Empty description={t('no_model_usage_data')} /> }}
          />
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between' }}>
        <h2><SafetyCertificateOutlined /> {t('rate_limit_management')}</h2>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>{t('refresh')}</Button>
          <Button type="primary" icon={<SafetyCertificateOutlined />} onClick={() => setConfigModalVisible(true)}>
            {t('config')}
          </Button>
        </Space>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('active_users')}
              value={overview?.active_users || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('active_keys')}
              value={overview?.active_keys || 0}
              prefix={<KeyOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('active_ips')}
              value={overview?.active_ips || 0}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('blocked_count')}
              value={blockedUsers.length}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: blockedUsers.length > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 当前限流配置 */}
      {config && (
        <Card title={t('current_config')} style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Tag color="blue" icon={<UserOutlined />} style={{ fontSize: 14, padding: '4px 8px' }}>
                {t('user_limit')}: {config.user_limit?.limit || 60}次/{config.user_limit?.window || 60}s
              </Tag>
            </Col>
            <Col span={6}>
              <Tag color="green" icon={<KeyOutlined />} style={{ fontSize: 14, padding: '4px 8px' }}>
                {t('key_limit')}: {config.key_limit?.limit || 100}次/{config.key_limit?.window || 60}s
              </Tag>
            </Col>
            <Col span={6}>
              <Tag color="purple" icon={<GlobalOutlined />} style={{ fontSize: 14, padding: '4px 8px' }}>
                {t('ip_limit')}: {config.ip_limit?.limit || 120}次/{config.ip_limit?.window || 60}s
              </Tag>
            </Col>
            <Col span={6}>
              <Tag color="orange" icon={<ApiOutlined />} style={{ fontSize: 14, padding: '4px 8px' }}>
                {t('model_limit')}: {Object.keys(config.model_limit || {}).length} {t('models_configured')}
              </Tag>
            </Col>
          </Row>
        </Card>
      )}

      {/* 被限流用户告警 */}
      {blockedUsers.length > 0 && (
        <Alert
          message={`${blockedUsers.length} ${t('users_blocked')}`}
          description={
            <div>
              {blockedUsers.slice(0, 5).map((user) => (
                <Tag key={user.user_id || user.id} color="red">{user.user_id}: {user.req_count} {t('requests')}</Tag>
              ))}
              {blockedUsers.length > 5 && <span>... +{blockedUsers.length - 5} {t('more')}</span>}
            </div>
          }
          type="warning"
          showIcon
          icon={<ThunderboltOutlined />}
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 用量统计表格 */}
      <Card>
        <Tabs items={tabItems} />
      </Card>

      {/* 限流配置弹窗 */}
      <Modal
        title={t('rate_limit_config')}
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={config}
          onFinish={handleUpdateConfig}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('user_limit')} name={['user_limit', 'limit']}>
                <InputNumber min={1} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('window_seconds')} name={['user_limit', 'window']}>
                <InputNumber min={1} max={3600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('key_limit')} name={['key_limit', 'limit']}>
                <InputNumber min={1} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('window_seconds')} name={['key_limit', 'window']}>
                <InputNumber min={1} max={3600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('ip_limit')} name={['ip_limit', 'limit']}>
                <InputNumber min={1} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('window_seconds')} name={['ip_limit', 'window']}>
                <InputNumber min={1} max={3600} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">{t('save')}</Button>
              <Button onClick={() => setConfigModalVisible(false)}>{t('cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 用户自定义限流弹窗 */}
      <Modal
        title={t('set_user_limit')}
        open={userLimitModalVisible}
        onCancel={() => setUserLimitModalVisible(false)}
        footer={null}
      >
        <Form
          form={userLimitForm}
          layout="vertical"
          onFinish={handleApplyUserLimit}
        >
          <Form.Item
            label={t('user_id')}
            name="user_id"
            rules={[{ required: true, message: t('required') }]}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            label={t('limit')}
            name="limit"
            rules={[{ required: true, message: t('required') }]}
          >
            <InputNumber min={1} max={1000} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label={t('window_seconds')}
            name="window_seconds"
            rules={[{ required: true, message: t('required') }]}
          >
            <InputNumber min={1} max={3600} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">{t('save')}</Button>
              <Button onClick={() => setUserLimitModalVisible(false)}>{t('cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 模型限流配置弹窗 */}
      <Modal
        title={editingModel ? t('edit_model_limit') : t('add_model_limit')}
        open={modelLimitModalVisible}
        onCancel={() => { setModelLimitModalVisible(false); setEditingModel(null); }}
        footer={null}
      >
        <Form
          form={modelLimitForm}
          layout="vertical"
          onFinish={handleAddModelLimit}
        >
          <Form.Item
            label={t('model_name')}
            name="model"
            rules={[{ required: true, message: t('required') }]}
            extra={editingModel ? null : t('model_name_hint')}
          >
            <Input placeholder="e.g. deepseek-v4-flash, qwen-turbo, gpt-4o" disabled={!!editingModel} />
          </Form.Item>
          <Form.Item
            label={t('limit')}
            name="limit"
            rules={[{ required: true, message: t('required') }]}
            extra={t('limit_per_window')}
          >
            <InputNumber min={1} max={10000} style={{ width: '100%' }} placeholder="e.g. 50" />
          </Form.Item>
          <Form.Item
            label={t('window_seconds')}
            name="window"
            rules={[{ required: true, message: t('required') }]}
            extra={t('window_seconds_hint')}
          >
            <InputNumber min={1} max={3600} style={{ width: '100%' }} placeholder="e.g. 60" />
          </Form.Item>
          <Divider />
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">{t('save')}</Button>
              <Button onClick={() => { setModelLimitModalVisible(false); setEditingModel(null); }}>{t('cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RateLimits;
